import { useState } from "react";
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";
import "./AppCTA.scss";

export type AppCTAVariant =
  | "primary"
  | "primary_dark"
  | "default"
  | "dashboard-success"
  | "dashboard-error";

export type AppCTASize = "large" | "medium" | "small";

export interface AppCTAProps {
  label: ReactNode;
  variant?: AppCTAVariant;
  size?: AppCTASize;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
}

const AppCTA = ({
  label,
  variant = "default",
  size = "large",
  type = "button",
  disabled = false,
  fullWidth = false,
  className = "",
  onClick,
}: AppCTAProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const classNames = [
    "app-cta",
    `app-cta-${variant}`,
    `app-cta-${size}`,
    fullWidth && "app-cta-full-width",
    isLoading && "app-cta-loading",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;

    setIsLoading(true);
    try {
      await onClick(event);
    } catch {
      // Swallowed here so a failing onClick can't surface as an unhandled promise
      // rejection from this button - callers handle their own error state.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      onClick={handleClick}
      className={classNames}
    >
      <span className={isLoading ? "app-cta-label app-cta-label-hidden" : "app-cta-label"}>
        {label}
      </span>
      {isLoading && (
        <span className="app-cta-dot-pulse" aria-hidden="true">
          <span className="app-cta-dot" />
          <span className="app-cta-dot" />
          <span className="app-cta-dot" />
        </span>
      )}
    </button>
  );
};

export default AppCTA;
